import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useViewerItemStore, ItemType } from '@/modules/viewer/store/useViewerItemStore';
import { useRedactionStore, RedactionResult } from '@/modules/viewer/store/useRedactionStore';
import { useUser } from '@/modules/viewer/composables/useUser';
import { useFeatures } from '@/composables/useFeatures';

export const useRedaction = () => {
  const viewerItemStore = useViewerItemStore();
  const redactionStore = useRedactionStore();
  const { type, fileExt, checksum, isEditProcessing } = storeToRefs(viewerItemStore);
  const { isCurrentItemOwner } = useUser();
  const { getFeature } = useFeatures();
  const redactionEnabled = getFeature('video-redaction', false);

  // Redaction covers text burned into the video, so it is offered on the mp4s
  // the renderer can repaint.
  const canRedact = computed(
    () => isCurrentItemOwner.value && redactionEnabled.value && redactionStore.isAvailable && type.value === ItemType.VIDEO && fileExt.value === '.mp4'
  );

  // `isStarting` counts as busy: the run is not `isRunning` until the request comes
  // back, and a second click in that window creates a second run.
  const isRedactBusy = computed(() => redactionStore.isRunning || redactionStore.isStarting);
  // A result describes one file. An edit or replace changes the item's checksum, and
  // the result no longer applies to what is on the page.
  const isResultForCurrentFile = computed(() => !!redactionStore.summary?.checksum && redactionStore.summary.checksum === checksum.value);

  // The server refuses a second run on a file it already redacted or found nothing in;
  // this only saves the click.
  const isRedactFinalForFile = computed(
    () => isResultForCurrentFile.value && (!!redactionStore.summary?.nothingToRedact || redactionStore.summary?.result === RedactionResult.REDACTED)
  );
  const isRedactDisabled = computed(() => isEditProcessing.value || isRedactBusy.value || isRedactFinalForFile.value);

  const redactionResultLabel = computed(() => {
    const summary = redactionStore.summary;
    if (isRedactBusy.value || !summary || !isResultForCurrentFile.value) {
      return null;
    }

    if (summary.result === RedactionResult.NOTHING_FOUND) {
      return 'No sensitive text found';
    }

    if (summary.result === RedactionResult.REDACTED) {
      return 'Sensitive text redacted';
    }

    return null;
  });

  return { canRedact, isRedactBusy, isRedactDisabled, redactionResultLabel };
};
